import { type DriverFormValues, DriverCreateValidation, DriverEditValidation } from '../data/DriverFormValidation'
import type { SubmitHandler } from 'react-hook-form'
import type { Driver } from '../../../../../types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormProvider, useForm, Controller, useWatch } from 'react-hook-form'
import { useLocation, useSearchParams } from 'react-router-dom'
import { FormatPhoneNo } from '../components/FormatPhoneNo'
import { Switch, SwitchThumb } from '@radix-ui/react-switch'
import { MessageForm } from '../components/MessageForm'
import { Separator } from '@radix-ui/react-separator'
import { changeDriver, createDriverWithDetails, fetchContactDetails, updateDriverDetails } from '../../../../../api/api'
import { toast } from '../../../../../toast'
import { employeeAccessEmail } from '../../../../../api/flow'
import { getContactErrorMessage } from '../../../../../utils/errorHandling'
import SignalSpinnerLoader from '../../../../../components/loaders/SignalSpinnerLoader'
import { Stack } from '../../../../cars/components/V3/_shared/Stack'
import { Input } from '../../../../cars/components/V3/_shared/Form/Input'
import { Select } from '../../../../cars/components/V3/_shared/Select'
import { PrimaryButton } from '../../../../cars/components/V3/_shared/Buttons/PrimaryButton'
import { FormAlert } from '../../Form/FormAlert'
import { Label } from '@radix-ui/react-label'
import { useUserContext } from '../../../../../context/UserProvider'

const CONTACT_TYPE_OPTIONS = [
  { value: 'Driver', label: 'Sjåfør' },
  { value: 'Person', label: 'Kontaktperson' },
]

export const FormDetailsMap = (details: any): DriverFormValues => ({
  name: details?.name ?? '',
  mail: details?.mail ?? details?.email ?? '',
  jobTitle: details?.jobTitle ?? '',
  contactType: details?.contactType ?? '',
  phoneNo: (details?.phoneNo ?? details?.phone ?? '').toString().replace(/\D/g, ''),
  address: details?.address ?? '',
  city: details?.city ?? '',
  postCode: details?.postCode ?? '',
  regNo: details?.regNo ?? '',
  isWebUser: Boolean(details?.isWebUser),

  webUser: {
    regNo: '',
    access: 'YES',
    filterAccess: [] as string[],
    message: '',
  },
})

type DriverFormProps = {
  onFinish: (createdWithCar: boolean) => void
  companyId?: string
  companyName: string
  driver?: Driver
  onDiscontinue?: () => void
}

export const DriverForm = ({ companyId, companyName, driver, ...props }: DriverFormProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [existingDetails, setExistingDetails] = useState<any>(null)

  // @ts-ignore  remove this ts-ignore when UserContext is type safe
  const { userData } = useUserContext()
  const submittedBy = userData?.user?.name || userData?.user?.contactDetails?.name || ''

  const location = useLocation()
  const [searchParams] = useSearchParams()
  const splitPath = location.pathname.split('/')
  const isOnCar = splitPath.map((i: string) => i.toLowerCase()).includes('cars') && splitPath.length > 2
  const carAccountNo = isOnCar && searchParams.get('type') !== 'budget' ? splitPath[2] : ''

  const method = useForm<DriverFormValues>({
    defaultValues: FormDetailsMap({}),
    resolver: (driver?.id ? DriverEditValidation : DriverCreateValidation) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const {
    control,
    reset,
    formState: { errors, isSubmitting, isValid, isDirty, dirtyFields },
  } = method

  const getContactDetails = useCallback(() => {
    const preferNonEmpty = (base: any, incoming: any) => {
      const out = { ...base, ...incoming }
      for (const k of Object.keys(base || {})) {
        const inc = incoming?.[k]
        if (inc === '' || inc === null || inc === undefined) out[k] = base?.[k]
      }
      return out
    }

    // Create driver
    if (!driver) {
      reset(FormDetailsMap({}), {
        keepErrors: false,
        keepDirty: false,
        keepTouched: false,
      })
      setIsLoading(false)
      return
    }

    // Edit driver
    setIsLoading(true)
    reset(FormDetailsMap(driver), {
      keepErrors: false,
      keepDirty: false,
      keepTouched: false,
    })

    fetchContactDetails(driver.id)
      .then((apiDetails) => {
        const merged = preferNonEmpty(driver, apiDetails)
        setExistingDetails(merged)

        reset(FormDetailsMap(merged), {
          keepErrors: false,
          keepDirty: false,
          keepTouched: false,
        })

        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
        toast.error('Error', `Fant ikke bruker med id: ${driver.id}`)
      })
  }, [driver, reset])

  useEffect(() => {
    getContactDetails()
  }, [getContactDetails])

  const hasChanges = driver?.id ? isDirty && Object.keys(dirtyFields || {}).length > 0 : true
  const hasWebAccess = useWatch({ control, name: 'isWebUser' })

  const toApiPayload = (values: DriverFormValues) => {
    const payload = {
      name: values.name,
      address: values.address,
      city: values.city,
      postCode: values.postCode,
      jobTitle: values.jobTitle,
      contactType: values.contactType,
      phone: values.phoneNo,
      mail: values.mail,
      isWebUser: values.isWebUser,
      regNo: values.regNo,
    }
    return payload
  }

  const toUpdatePayload = (values: DriverFormValues) => {
    return {
      name: values.name,
      address: values.address,
      postCode: values.postCode,
      jobTitle: values.jobTitle,
      city: values.city,
      contactType: values.contactType || existingDetails?.contactType,
      phone: method.formState.dirtyFields?.phoneNo
        ? values.phoneNo
        : (existingDetails?.phone ?? existingDetails?.phoneNo ?? values.phoneNo),
      mail: method.formState.dirtyFields?.mail
        ? values.mail
        : (existingDetails?.email ?? existingDetails?.mail ?? values.mail),
    }
  }
  const submittingRef = useRef(false)

  const onSubmit: SubmitHandler<DriverFormValues> = async (details) => {
    if (submittingRef.current) return
    submittingRef.current = true

    try {
      let createdWithCar = false

      if (companyId && driver?.id) {
        await updateDriverDetails(companyId, driver.id, toUpdatePayload(details))
        toast.success('Lagret', 'Bruker er lagret.')
      } else if (!driver && carAccountNo) {
        const createdId = await createDriverWithDetails(companyId, toApiPayload(details))
        await changeDriver(carAccountNo, createdId)
        createdWithCar = true
        toast.success('Opprettet', 'Ny bruker er opprettet.')
      } else {
        await createDriverWithDetails(companyId, toApiPayload(details))
        createdWithCar = false
        toast.success('Opprettet', 'Ny bruker er opprettet.')
      }

      // On edit, we only save contact data and do not send access email.
      if (driver?.id || !details.isWebUser) {
        props.onFinish(createdWithCar)
        return
      }

      await employeeAccessEmail({
        company: companyName,
        name: details.name,
        submittedBy,
        message: details.webUser?.message,
        access: details.webUser?.access,
        filterAccess: details.webUser?.filterAccess,
        regNo: details.webUser?.regNo,
      })

      props.onFinish(createdWithCar)
    } catch (err: any) {
      const code = err?.response?.data?.code
      if (code === 'DUPLICATE_PHONE_NO') {
        method.setError('phoneNo', { type: 'server', message: 'Telefonnummeret er allerede i bruk' })
        method.setFocus('phoneNo')
        toast.error('Duplikat', 'Telefon nummer finnes allerede på annen bruker')
        return
      }
      window.alert(getContactErrorMessage(err))
      console.log(err)
    } finally {
      submittingRef.current = false
    }
  }

  if (isLoading) return <SignalSpinnerLoader />

  return (
    <FormProvider {...method}>
      <form onSubmit={method.handleSubmit(onSubmit, (errs) => console.log('INVALID', errs))}>
        <Stack spacing="sm">
          <Stack spacing="xs">
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <Input
                  id="name"
                  placeholder="Navn"
                  label="Fullt navn *"
                  value={field.value}
                  onChange={field.onChange}
                  required
                />
              )}
            />
            {errors.name?.message && <FormAlert errorMessage={errors.name?.message} />}
          </Stack>

          <div className="flex gap-2">
            <Stack spacing="xs" className="w-full">
              <Controller
                control={control}
                name="jobTitle"
                render={({ field }) => (
                  <Input
                    id="jobTitle"
                    placeholder="Stilling"
                    label="Stilling"
                    className="h-[46px]"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.jobTitle?.message && <FormAlert errorMessage={errors.jobTitle?.message} />}
            </Stack>

            {!driver?.id && (
              <Stack spacing="xs" className="w-full">
                <Controller
                  control={control}
                  name="contactType"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-[14px] font-semibold" htmlFor="contactType">
                        Kontakt type
                      </Label>
                      <Select
                        className="h-[46px] w-full rounded-md border-borderGray bg-white px-4 py-3 text-base text-secondaryText shadow-soft"
                        placeholder="Velg kontakt type"
                        options={CONTACT_TYPE_OPTIONS}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                      />
                    </div>
                  )}
                />
                {errors.contactType?.message && <FormAlert errorMessage={errors.contactType?.message} />}
              </Stack>
            )}
          </div>

          <div className="flex w-full gap-2">
            <Stack spacing="xs">
              <Controller
                control={control}
                name="mail"
                render={({ field }) => (
                  <Input
                    id="mail"
                    placeholder="E-post"
                    label="E-post *"
                    value={field.value}
                    onChange={field.onChange}
                    required
                  />
                )}
              />
              {errors.mail?.message && <FormAlert errorMessage={errors.mail?.message} />}
            </Stack>

            <div className="w-full">
              <Stack spacing="xs">
                <Controller
                  control={control}
                  name="phoneNo"
                  render={({ field }) => (
                    <Input
                      id="phoneNo"
                      label="Telefon *"
                      placeholder="987 65 432"
                      inputMode="numeric"
                      value={FormatPhoneNo(field.value)}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\D/g, '')
                        field.onChange(raw)
                      }}
                      required
                    />
                  )}
                />
                {errors.phoneNo?.message && <FormAlert errorMessage={errors.phoneNo?.message} />}
              </Stack>
            </div>
          </div>

          <Stack spacing="xs" className="w-full">
            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <Input
                  id="address"
                  placeholder="Adresse"
                  label="Adresse"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.address?.message && <FormAlert errorMessage={errors.address?.message} />}
          </Stack>

          <div className="flex gap-2">
            <Stack spacing="xs" className="w-full">
              <Controller
                control={control}
                name="city"
                render={({ field }) => (
                  <Input id="city" placeholder="By" label="By" value={field.value} onChange={field.onChange} />
                )}
              />
            </Stack>

            <Stack spacing="xs" className="w-[40%]">
              <Controller
                control={control}
                name="postCode"
                render={({ field }) => <Input id="postCode" label="Postnummer" placeholder="1234" {...field} />}
              />
              {errors.postCode?.message && <FormAlert errorMessage={errors.postCode?.message} />}
            </Stack>
          </div>

          {!driver && (
            <div className="flex flex-col gap-3">
              <Separator className="my-[15px] bg-grayDark data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />
              <div className="font-semibold text-[16px]">Legg til web tilgang</div>
              <Controller
                control={control}
                name="isWebUser"
                render={({ field }) => (
                  <div className="flex items-center gap-6">
                    Skal den nye ansatte ha web tilgang?
                    <Switch
                      id="carConfig"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="relative h-[22px] w-[42px] cursor-default rounded-full bg-disabledBg data-[state=checked]:bg-primaryBlue"
                    >
                      <SwitchThumb className="block size-[18px] translate-x-0.5 rounded-full bg-white transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[22px]" />
                    </Switch>
                  </div>
                )}
              />
              {hasWebAccess && (
                <div className="mt-4">
                  <MessageForm />
                </div>
              )}
            </div>
          )}

          {isSubmitting ? (
            <SignalSpinnerLoader />
          ) : (
            <div className="flex w-full justify-end mt-8">
              {driver?.id ? (
                <div className="flex w-full justify-between">
                  <PrimaryButton
                    buttonType="button"
                    variant="danger"
                    disabled={!props.onDiscontinue}
                    onClick={() => {
                      props.onDiscontinue?.()
                    }}
                  >
                    Avslutt ansatt
                  </PrimaryButton>
                  <PrimaryButton buttonType="submit" disabled={isSubmitting || !isValid || !hasChanges}>
                    Lagre
                  </PrimaryButton>
                </div>
              ) : (
                <PrimaryButton buttonType="submit" disabled={isSubmitting || !isValid || !hasChanges}>
                  {hasWebAccess ? 'Lagre og send melding' : 'Lagre ansatt'}
                </PrimaryButton>
              )}
            </div>
          )}
        </Stack>
      </form>
    </FormProvider>
  )
}
